/**
 * adContext - utilities for resolving the viewer's state. The legacy
 * advertisingService.getUserState() is private; this helper duplicates
 * just enough logic to give public-page components access without
 * coupling to the workspace module.
 *
 * Resolution order:
 *   1. Logged-in user's `profiles.state` in Supabase (authoritative).
 *   2. Header state switcher value in localStorage (bp:userState).
 *   3. null (serves any-state inventory as a last resort).
 *
 * Step 2 was added 2026-09-08 to fix a bug where anonymous visitors
 * with a state picked in the header were being served ads from every
 * state - because getViewerState() only knew about logged-in profiles.
 */
import { getSupabase } from '../../workspace/lib/supabaseClient';
import { STATE_STORAGE_KEY, STATE_CHANGE_EVENT } from '../../lib/userState';

const STATE_CODES = {
  sa: 'SA', 'south australia': 'SA',
  nsw: 'NSW', 'new south wales': 'NSW',
  vic: 'VIC', victoria: 'VIC',
  qld: 'QLD', queensland: 'QLD',
  wa: 'WA', 'western australia': 'WA',
  tas: 'TAS', tasmania: 'TAS',
  act: 'ACT', 'australian capital territory': 'ACT',
  nt: 'NT', 'northern territory': 'NT',
};

const normalise = (raw) => {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase();
  return STATE_CODES[key] || null;
};

let cached = { value: null, at: 0 };
const TTL = 5 * 60 * 1000;

export const getViewerState = async () => {
  if (cached.value && Date.now() - cached.at < TTL) return cached.value;

  // 1) Authoritative signal: logged-in user's profile.state.
  try {
    const sb = getSupabase();
    const { data: { user } } = await sb.auth.getUser();
    if (user) {
      const { data } = await sb.from('profiles').select('state').eq('id', user.id).maybeSingle();
      const state = normalise(data?.state);
      if (state) {
        cached = { value: state, at: Date.now() };
        return state;
      }
    }
  } catch (_e) { /* fall through */ }

  // 2) Anonymous fallback: header state switcher (localStorage).
  //    Fixes: anonymous VIC visitor now sees only VIC-targeted ads
  //    instead of the whole any-state pool.
  try {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem(STATE_STORAGE_KEY);
      const state = normalise(raw);
      if (state) {
        cached = { value: state, at: Date.now() };
        return state;
      }
    }
  } catch (_e) { /* localStorage disabled - fall through */ }

  // 3) No signal - serve any-state inventory.
  cached = { value: null, at: Date.now() };
  return null;
};

// Auto-invalidate the cache when the user picks a new state via the
// header switcher, so the next getRandomAdvert() call sees ads for the
// newly-picked state without a page reload.
if (typeof window !== 'undefined') {
  window.addEventListener(STATE_CHANGE_EVENT, () => {
    cached = { value: null, at: 0 };
  });
}

export const clearViewerStateCache = () => {
  cached = { value: null, at: 0 };
};
